import * as React from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Boxes, ClipboardList, FileCheck2, LayoutDashboard, Receipt, UserRoundCog, Users2 } from "lucide-react";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useUserProfile } from "@/hooks/useUserProfile";
import { DashboardTab } from "./components/DashboardTab";
import { InventoryTab } from "./components/InventoryTab";
import { OrdersTab } from "./components/OrdersTab";
import { ContractsTab } from "./components/ContractsTab";
import { UsersTab } from "./components/UsersTab";
import { CommissionsTab } from "./components/CommissionsTab";
import { AccountRecoveryCard } from "./components/AccountRecoveryCard";


const tabs = [
  { value: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { value: "inventory", label: "Inventory", icon: Boxes },
  { value: "orders", label: "Orders", icon: ClipboardList },
  { value: "contracts", label: "Contracts", icon: FileCheck2 },
  { value: "users", label: "Users", icon: Users2 },
  { value: "recovery", label: "Recovery", icon: UserRoundCog },
  { value: "commissions", label: "Commissions", icon: Receipt },
] as const;

type AdminTab = (typeof tabs)[number]["value"];
const validTabs = new Set<AdminTab>(tabs.map((tab) => tab.value));

export default function AdminPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, authUserId } = useUserProfile();

  const urlTab = searchParams.get("tab") as AdminTab | null;
  const activeTab: AdminTab = urlTab && validTabs.has(urlTab) ? urlTab : "dashboard";
  const monthParam = searchParams.get("month") ?? undefined;

  const handleTabChange = React.useCallback(
    (nextTab: string) => {
      if (nextTab === "dashboard") {
        navigate("/admin", { replace: true });
      } else {
        navigate(`/admin?tab=${nextTab}`, { replace: true });
      }
    },
    [navigate],
  );

  React.useEffect(() => {
    const targetId = location.hash.slice(1);
    if (!targetId) return;

    const frame = window.requestAnimationFrame(() => {
      const target = document.getElementById(targetId);
      if (!(target instanceof HTMLElement)) return;
      target.scrollIntoView({ block: "start" });
      target.focus({ preventScroll: true });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [activeTab, location.hash]);

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <div className="max-w-full overflow-x-auto pb-1">
          <TabsList className="w-max">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <TabsTrigger key={tab.value} value={tab.value} className="gap-2">
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </div>

        <TabsContent value="dashboard">
          <DashboardTab authUserId={authUserId ?? undefined} user={user} />
        </TabsContent>

        <TabsContent value="inventory">
          <InventoryTab authUserId={authUserId ?? undefined} />
        </TabsContent>

        <TabsContent value="orders">
          <OrdersTab authUserId={authUserId ?? undefined} orderScope="orders" />
        </TabsContent>

        <TabsContent value="contracts">
          <ContractsTab authUserId={authUserId ?? undefined} />
        </TabsContent>

        <TabsContent value="users">
          <UsersTab authUserId={authUserId ?? undefined} />
        </TabsContent>

        <TabsContent value="recovery">
          <AccountRecoveryCard authUserId={authUserId ?? undefined} />
        </TabsContent>

        <TabsContent value="commissions">
          <CommissionsTab authUserId={authUserId ?? undefined} initialMonth={monthParam} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
